import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

type Status =
  | 'processing'
  | 'employee-picture-uploaded'
  | 'invalid-image'
  | 'collection-for-company-not-created'
  | 'service-unavailable'
  | 'employee-clocked-in'
  | 'employee-clocked-out'
  | 'error-when-clocking'

interface IClockPicture {
  id: number
  clock_time: string
  created_at: number
  filename: string
  content_type: string
  status: Status
  message: string | null
  identifier: string | null
  employee_id: number | null
  url: string
}

class ClockPicture extends Model<IClockPicture> {
  constructor(attr: IClockPicture) {
    super(attr)

    makeObservable(this, {
      isProcessing: computed,
      isInvalid: computed,
    })
  }

  get isProcessing(): boolean {
    return this.attributes.status === 'processing'
  }

  get isInvalid(): boolean {
    return [
      'invalid-image',
      'collection-for-company-not-created',
      'service-unavailable',
      'error-when-clocking',
    ].includes(this.attributes.status)
  }

  get downloadUrl(): string {
    return buildUrl(`/${this.url()}/download`)
  }

  get filename(): string {
    return this.attributes.filename
  }

  get status(): string {
    switch (this.attributes.status) {
      case 'employee-clocked-in':
        return i18n.t('time_tracking.settings.face_recognition.status.clock_in')
      case 'employee-clocked-out':
        return i18n.t('time_tracking.settings.face_recognition.status.clock_out')

      default:
        return this.attributes.status
    }
  }

  get description(): string {
    return `${this.status} - ${moment.unix(this.attributes.created_at).format('ll')}`
  }

  get clockedIn(): boolean {
    return this.attributes.status === 'employee-clocked-in'
  }

  get clockedOut(): boolean {
    return this.attributes.status === 'employee-clocked-out'
  }

  static urlRoot() {
    return '/face_recognition/clock_pictures'
  }
}

export default ClockPicture
